<template>
  <ListItem
    :to="{
      name: 'paymentTerminal',
      params: { id: paymentTerminal.id },
    }"
    :drop-down-items="dropDownItems"
  >
    <div class="grid w-full gap-1 py-1 pl-3 pr-2 text-sm md:flex md:items-center">
      <div class="basis-[50%]">
        <p class="font-bold">{{ paymentTerminal.description }}</p>
        <p>{{ paymentTerminal.terminalId }}</p>
      </div>

      <div class="basis-[50%]">
        <p>
          <MapPinIcon class="inline-icon text-obsidian-400" />
          {{ getLocalizedField(paymentTerminal.station.info) }}
        </p>
      </div>
    </div>
  </ListItem>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import type { DropdownItem } from '@/components/DropdownDotted.vue';
import { Alert } from '@/services/alert.service';
import type { PaymentTerminal } from '@/entities/payment-terminals/payment-terminal.entity';
import { useDeletePaymentTerminal } from '@/queries/use-payment-terminals';
import router from '@/router';
import { useLocalizedFields } from '@/hooks/use-localized-fields';
import ListItem from '@/components/ListItem.vue';
import { MapPinIcon } from 'lucide-vue-next';

const props = defineProps<{
  paymentTerminal: PaymentTerminal;
}>();

const { t } = useI18n();
const { getLocalizedField } = useLocalizedFields();

const { mutateAsync: removePaymentTerminal } = useDeletePaymentTerminal();

const deletePaymentTerminal = async (paymentTerminalId: string) => {
  const alertResult = await Alert.fire({
    titleText: t('reallyDeletePaymentTerminalTitle'),
    text: t('reallyCancelPaymentTerminalText'),
    icon: 'warning',
    showDenyButton: true,
    confirmButtonText: t('deletePaymentTerminal'),
    denyButtonText: t('dontDelete'),
  });
  if (alertResult.isConfirmed) {
    removePaymentTerminal(paymentTerminalId);
  }
};

const dropDownItems: DropdownItem[] = [
  {
    title: t('editPaymentTerminal'),
    onClick: () =>
      router.push({
        name: 'paymentTerminal',
        params: { id: props.paymentTerminal.id },
      }),
  },
  {
    title: t('deletePaymentTerminal'),
    onClick: () => deletePaymentTerminal(props.paymentTerminal.id),
  },
];
</script>

<i18n lang="json">
{
  "en": {
    "description": "Description",
    "terminalId": "Terminal ID",
    "station": "Station",
    "editPaymentTerminal": "Edit Payment Terminal",
    "deletePaymentTerminal": "Delete Payment Terminal",
    "reallyDeletePaymentTerminalTitle": "Really delete Payment Terminal?",
    "reallyCancelPaymentTerminalText": "The Payment Terminal will be permanently deleted.",
    "dontDelete": "Don't delete"
  },
  "de": {
    "description": "Beschreibung",
    "terminalId": "Terminal ID",
    "station": "Station",
    "editPaymentTerminal": "Zahlungsterminal bearbeiten",
    "deletePaymentTerminal": "Zahlungsterminal löschen",
    "reallyDeletePaymentTerminalTitle": "Zahlungsterminal wirklich löschen?",
    "reallyCancelPaymentTerminalText": "Das Zahlungsterminal wird unwiderruflich gelöscht.",
    "dontDelete": "Nicht löschen"
  },
  "es": {
    "description": "Descripción",
    "terminalId": "ID del terminal",
    "station": "Estación",
    "editPaymentTerminal": "Editar terminal de pago",
    "deletePaymentTerminal": "Eliminar terminal de pago",
    "reallyDeletePaymentTerminalTitle": "¿Eliminar el terminal de pago?",
    "reallyCancelPaymentTerminalText": "El terminal de pago se eliminará de forma definitiva.",
    "dontDelete": "No eliminar"
  }
}
</i18n>
